import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SoiDaoBoard } from '../components/soi-dao/SoiDaoBoard';
import { SoiDaoHistory } from '../components/soi-dao/SoiDaoHistory';
import { SoiDaoPlayBar } from '../components/soi-dao/SoiDaoPlayBar';
import { SoiDaoPlaysPanel } from '../components/soi-dao/SoiDaoPlaysPanel';
import { SoiDaoPrizePanel } from '../components/soi-dao/SoiDaoPrizePanel';
import { SoiDaoRoundHeader } from '../components/soi-dao/SoiDaoRoundHeader';
import { SoiDaoWalletPanel } from '../components/soi-dao/SoiDaoWalletPanel';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { soiDaoService } from '../services/soiDaoService';
import { getApiErrorMessage } from '../utils/apiError';
import { showError, showSuccess } from '../utils/swal';

const POLL_MS = 2000;

export function SoiDaoRoomPage() {
  const { roomId } = useParams();
  const { isAuthenticated } = useAuth();
  const { balance, setBalance, refreshWallet } = useWallet();
  const [roundData, setRoundData] = useState(null);
  const [history, setHistory] = useState([]);
  const [plays, setPlays] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadRoomData = useCallback(
    async (id) => {
      const requests = [
        soiDaoService.getRound(id),
        soiDaoService.getHistory(id),
      ];
      if (isAuthenticated) {
        requests.push(soiDaoService.getPlays({ roomId: id, page: 1, limit: 20 }));
      }

      const [roundRes, historyRes, playsRes] = await Promise.all(requests);
      setRoundData(roundRes);
      setHistory(historyRes || []);
      if (playsRes) setPlays(playsRes.items || []);
      setNotFound(false);

      setSelected((current) => {
        const available = new Set(
          (roundRes.cells || [])
            .filter((cell) => cell.state === 'available')
            .map((cell) => cell.position),
        );
        return current.filter((position) => available.has(position));
      });
    },
    [isAuthenticated],
  );

  useEffect(() => {
    if (!roomId) return undefined;

    loadRoomData(roomId).catch(() => setNotFound(true));
    const timer = setInterval(() => {
      loadRoomData(roomId).catch(() => {});
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [roomId, loadRoomData]);

  const handleToggle = (position) => {
    setSelected((current) =>
      current.includes(position)
        ? current.filter((item) => item !== position)
        : [...current, position],
    );
    setMessage('');
  };

  const handlePlay = (payload) => {
    setIsSubmitting(true);
    setMessage('');

    return soiDaoService
      .play(payload)
      .then(async (result) => {
        if (typeof result.balance === 'number') {
          setBalance(result.balance);
        }
        const okMessage = result.message || 'ซื้อดาวสำเร็จ';
        setMessage(okMessage);
        setSelected([]);
        await showSuccess({ title: 'ซื้อสำเร็จ', text: okMessage });
        return loadRoomData(roomId).then(() => {
          refreshWallet().catch(() => {});
          return true;
        });
      })
      .catch(async (error) => {
        const errMessage = getApiErrorMessage(error, 'ไม่สามารถซื้อดาวได้');
        setMessage(errMessage);
        await showError({ title: 'ซื้อไม่สำเร็จ', text: errMessage });
        return false;
      })
      .finally(() => setIsSubmitting(false));
  };

  if (notFound) {
    return <Navigate to="/soi-dao" replace />;
  }

  const room = roundData?.room;
  const cells = roundData?.cells || [];

  return (
    <div className="space-y-6 pb-28 sm:pb-6">
      <Link
        to="/soi-dao"
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-brand-red"
      >
        <ArrowLeft className="h-4 w-4" />
        กลับหน้ารวมห้องสอยดาว
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SoiDaoRoundHeader
            room={room}
            round={roundData?.round}
            remainingTime={roundData?.remainingTime}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">กระดานดาว 00–99</CardTitle>
              <p className="text-xs text-gray-500">
                เลือกดาว ชำระเงิน และลุ้นเลขผลได้ทันที
              </p>
            </CardHeader>
            <CardContent>
              {cells.length ? (
                <SoiDaoBoard
                  cells={cells}
                  selected={selected}
                  onToggle={handleToggle}
                />
              ) : (
                <p className="text-sm text-gray-500">กำลังโหลดกระดาน...</p>
              )}
            </CardContent>
          </Card>

          <SoiDaoPlayBar
            room={room}
            selected={selected}
            balance={balance}
            isAuthenticated={isAuthenticated}
            isSubmitting={isSubmitting}
            message={message}
            onClear={() => setSelected([])}
            onPlay={handlePlay}
          />
        </div>

        <div className="space-y-6">
          <SoiDaoWalletPanel />
          <SoiDaoPrizePanel
            prizes={roundData?.prizes || room?.prizes || []}
            entryCount={roundData?.round?.entryCount ?? 0}
          />
          <SoiDaoPlaysPanel plays={plays} />
          <SoiDaoHistory items={history} />
        </div>
      </div>
    </div>
  );
}
